import Ajv, { ErrorObject } from 'ajv'
import {
  JSONSchemaType,
  resolveEffectiveSchema,
  resolveEffectiveSchemaForArrayItem,
} from './resolveEffectiveSchema'
import { stripModel } from './stripModel'
import { TokenContext } from '../core/TokenContext'

export interface FieldProps {
  label?: string
  required?: boolean
  disabled?: boolean
  readOnly?: boolean
  placeholder?: string
  options?: Array<{ label: string; value: unknown }>
  min?: number
  max?: number
  step?: number
  minCount?: number
  maxCount?: number
  type?: string
  fieldType?: unknown
  allowedValues?: unknown[]
  field?: JSONSchemaType
  [key: string]: unknown
}

export interface FieldError {
  name: string
  message: string
  keyword?: string
}

/**
 * EffectiveSchemaBridge: the core adapter between a JSON Schema
 * and form components. Unlike the standard uniforms JSONSchemaBridge,
 * this bridge dynamically resolves conditionals against model data.
 */
export class EffectiveSchemaBridge {
  private readonly schema: JSONSchemaType
  private readonly validator: Ajv
  private _effectiveSchema: JSONSchemaType
  private _model: Record<string, unknown>
  private readonly tokenContext?: TokenContext

  constructor(
    schema: JSONSchemaType,
    model: Record<string, unknown> = {},
    ajv?: Ajv,
    tokenContext?: TokenContext
  ) {
    this.schema = schema
    this._model = model
    this.validator = ajv ?? new Ajv({
      allErrors: true,
      strict: false,
      validateFormats: false,
    })
    this.tokenContext = tokenContext
    this._effectiveSchema = resolveEffectiveSchema(schema, model, schema, this.validator, tokenContext)
  }

  /**
   * Recompute the effective schema against new model data.
   * Returns a new bridge instance if the effective schema changed.
   */
  updateModel(model: Record<string, unknown>): EffectiveSchemaBridge {
    // Always return a new bridge so React can detect changes
    const bridge = new EffectiveSchemaBridge(this.schema, model, this.validator, this.tokenContext)
    return bridge
  }

  /**
   * Get the raw (input) schema.
   */
  getRawSchema(): JSONSchemaType {
    return this.schema
  }

  /**
   * Get the current effective schema.
   */
  getEffectiveSchema(): JSONSchemaType {
    return this._effectiveSchema
  }

  /**
   * Get the effective schema for a specific array item.
   * Uses the RAW (un-resolved) schema to preserve conditionals for per-item evaluation.
   */
  getEffectiveSchemaForArrayItem(fieldName: string, itemIndex: number): JSONSchemaType {
    // Navigate the RAW schema to find the array field with conditionals intact
    const rawFieldDef = this.getRawField(fieldName)
    if (!rawFieldDef || rawFieldDef.type !== 'array' || !rawFieldDef.items) {
      throw new Error(`${fieldName} is not an array field`)
    }

    const itemsSchema = Array.isArray(rawFieldDef.items)
      ? rawFieldDef.items[itemIndex] ?? rawFieldDef.items[rawFieldDef.items.length - 1]
      : rawFieldDef.items

    const items = (this._model[fieldName] ?? []) as unknown[]
    const itemData = items[itemIndex] ?? {}

    return resolveEffectiveSchemaForArrayItem(itemsSchema as JSONSchemaType, itemData, this.schema, this.validator, this.tokenContext)
  }

  /**
   * Navigate the raw (un-resolved) schema to get a field definition.
   * This preserves conditionals (if/then/else, allOf, etc.) for later resolution.
   */
  getRawField(name: string): JSONSchemaType {
    if (!name) return this.schema

    const parts = name.split('.')
    let current = this.schema

    for (const part of parts) {
      if (part === '$' || !isNaN(Number(part))) {
        if (current.type === 'array' && current.items) {
          if (Array.isArray(current.items)) {
            const idx = part === '$' ? 0 : Number(part)
            current = current.items[idx] ?? current.items[0]
          } else {
            current = current.items as JSONSchemaType
          }
        } else {
          throw new Error(`Cannot access array item at ${name}`)
        }
      } else {
        if (current.properties && current.properties[part]) {
          current = current.properties[part]
        } else {
          throw new Error(`Field "${part}" not found in raw schema (path: ${name})`)
        }
      }
    }

    return current
  }

  /**
   * Get the field definition for a dotted path like "address.street".
   * For array item paths (numeric indices), resolves per-item conditionals
   * against the actual item data.
   */
  getField(name: string): JSONSchemaType {
    if (!name) return this._effectiveSchema

    const parts = name.split('.')
    let current = this._effectiveSchema

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i]
      if (part === '$' || !isNaN(Number(part))) {
        // Array item access
        if (current.type === 'array' && current.items) {
          const itemsSchema = Array.isArray(current.items)
            ? current.items[part === '$' ? 0 : Number(part)] ?? current.items[0]
            : (current.items as JSONSchemaType)

          if (part !== '$') {
            // Resolve per-item conditionals against actual item data
            const arrayPath = parts.slice(0, i).join('.')
            const arrayData = (this.getModelValue(arrayPath) ?? []) as unknown[]
            const itemData = arrayData[Number(part)] ?? {}
            current = resolveEffectiveSchemaForArrayItem(itemsSchema, itemData, this.schema, this.validator, this.tokenContext)
          } else {
            current = itemsSchema
          }
        } else {
          throw new Error(`Cannot access array item at ${name}`)
        }
      } else {
        if (current.properties && current.properties[part]) {
          current = current.properties[part]
        } else {
          throw new Error(`Field "${part}" not found in effective schema (path: ${name})`)
        }
      }
    }

    return current
  }

  private getModelValue(path: string): unknown {
    if (!path) return this._model
    const parts = path.split('.')
    let current: unknown = this._model
    for (const part of parts) {
      if (current == null) return undefined
      if (Array.isArray(current)) {
        current = current[Number(part)]
      } else if (typeof current === 'object') {
        current = (current as Record<string, unknown>)[part]
      } else {
        return undefined
      }
    }
    return current
  }

  /**
   * Get the subfields (property names) for a given path.
   */
  getSubfields(name?: string): string[] {
    const field = name ? this.getField(name) : this._effectiveSchema

    if (field.type === 'array' && field.items) {
      const items = Array.isArray(field.items) ? field.items[0] : field.items
      return (items as JSONSchemaType).properties
        ? Object.keys((items as JSONSchemaType).properties!)
        : []
    }

    return field.properties ? Object.keys(field.properties) : []
  }

  /**
   * Get the JavaScript type for a field.
   */
  getType(name: string): unknown {
    const field = this.getField(name)
    const typeStr = Array.isArray(field.type) ? field.type[0] : field.type

    switch (typeStr) {
      case 'string':
        return String
      case 'number':
      case 'integer':
        return Number
      case 'boolean':
        return Boolean
      case 'object':
        return Object
      case 'array':
        return Array
      default:
        return String
    }
  }

  /**
   * Get the component props for a field.
   */
  getProps(name: string): FieldProps {
    const field = this.getField(name)
    const props: FieldProps = { field }

    // Label
    props.label =
      field.title ??
      name
        .split('.')
        .pop()
        ?.replace(/([A-Z])/g, ' $1')
        .trim()
        .replace(/^./, c => c.toUpperCase())

    // Required
    const parentPath = name.split('.').slice(0, -1).join('.')
    const fieldName = name.split('.').pop()!
    const parent = parentPath ? this.getField(parentPath) : this._effectiveSchema

    // Check if parent's required array contains this field
    if (parent.type === 'array' && parent.items) {
      const items = Array.isArray(parent.items) ? parent.items[0] : parent.items
      props.required = (items as JSONSchemaType).required?.includes(fieldName) ?? false
    } else {
      props.required = parent.required?.includes(fieldName) ?? false
    }

    // Type mapping
    const typeStr = Array.isArray(field.type) ? field.type[0] : field.type
    props.type = typeStr

    // Enum → options
    if (field.enum) {
      props.options = field.enum.map(value => ({
        label: String(value),
        value,
      }))
      props.allowedValues = field.enum
    }

    // Numeric constraints
    if (field.minimum !== undefined) props.min = field.minimum
    if (field.maximum !== undefined) props.max = field.maximum
    if (field.multipleOf !== undefined) props.step = field.multipleOf

    // Array constraints
    if (field.minItems !== undefined) props.minCount = field.minItems
    if (field.maxItems !== undefined) props.maxCount = field.maxItems

    // Placeholder
    if (field.description) props.placeholder = field.description

    // Pass through uniforms overrides
    if (field.uniforms && typeof field.uniforms === 'object') {
      Object.assign(props, field.uniforms)
    }

    return props
  }

  /**
   * Get the initial/default value for a field.
   */
  getInitialValue(name: string): unknown {
    const field = this.getField(name)

    if (field.default !== undefined) return field.default

    const typeStr = Array.isArray(field.type) ? field.type[0] : field.type

    switch (typeStr) {
      case 'string':
        return ''
      case 'number':
      case 'integer':
        return undefined
      case 'boolean':
        return false
      case 'object': {
        const result: Record<string, unknown> = {}
        if (field.properties) {
          for (const [key, propSchema] of Object.entries(field.properties)) {
            const childPath = name ? `${name}.${key}` : key
            result[key] = this.getInitialValue(childPath)
          }
        }
        return result
      }
      case 'array': {
        if (!field.minItems) return []
        const items: unknown[] = []
        for (let i = 0; i < field.minItems; i++) {
          items.push(this.getInitialValue(`${name}.$`))
        }
        return items
      }
      default:
        return undefined
    }
  }

  /**
   * Get the initial model (all fields with defaults).
   */
  getInitialModel(): Record<string, unknown> {
    const model: Record<string, unknown> = {}
    for (const key of this.getSubfields()) {
      model[key] = this.getInitialValue(key)
    }
    return model
  }

  /**
   * When allErrors:true is set, Ajv emits one sub-error per failing oneOf/anyOf
   * branch (keyword: 'const' | 'enum') plus a summary error (keyword: 'oneOf' |
   * 'anyOf') for the same instancePath.  Keep only the summary error so callers
   * see a single error per field instead of N+1.
   */
  private static deduplicateCompositeErrors(errors: ErrorObject[]): ErrorObject[] {
    // 'errorMessage' is what ajv-errors emits after replacing a oneOf/anyOf error
    const compositeKeywords = new Set(['oneOf', 'anyOf', 'errorMessage'])
    const branchKeywords = new Set(['const', 'enum'])

    const pathsWithComposite = new Set(
      errors.filter(e => compositeKeywords.has(e.keyword)).map(e => e.instancePath)
    )
    if (pathsWithComposite.size === 0) return errors

    return errors.filter(
      e => !(branchKeywords.has(e.keyword) && pathsWithComposite.has(e.instancePath))
    )
  }

  private mapErrors(errors: ErrorObject[]): FieldError[] {
    if (errors.length > 0) {
      console.debug('[EffectiveSchemaBridge.mapErrors] Processing errors:', errors.map(e => ({
        keyword: e.keyword,
        path: e.instancePath,
        message: e.message,
      })))
    }
    return EffectiveSchemaBridge.deduplicateCompositeErrors(errors).map(err => ({
      name: (err.instancePath || '').replace(/^\//, '').replace(/\//g, '.'),
      message: err.message ?? 'Validation error',
      keyword: err.keyword,
    }))
  }

  /**
   * Validate the model against the FULL (non-effective) schema.
   */
  validate(model: Record<string, unknown>): FieldError[] {
    try {
      const validate = this.validator.compile(this.schema)
      const valid = validate(model)
      if (valid) return []
      return this.mapErrors(validate.errors ?? [])
    } catch {
      return []
    }
  }

  /**
   * Validate the model against the effective schema only.
   */
  validateEffective(model: Record<string, unknown>): FieldError[] {
    try {
      const validate = this.validator.compile(this._effectiveSchema)
      const valid = validate(model)
      if (valid) return []
      return this.mapErrors(validate.errors ?? [])
    } catch (err) {
      console.error('[EffectiveSchemaBridge.validateEffective] Schema compilation/validation failed:', err)
      return []
    }
  }

  /**
   * Strip the model to only contain effective properties.
   */
  stripModel(model: Record<string, unknown>): Record<string, unknown> {
    return stripModel(model, this._effectiveSchema, this.validator) as Record<string, unknown>
  }

  /**
   * Check if a field exists in the current effective schema.
   */
  hasField(name: string): boolean {
    try {
      this.getField(name)
      return true
    } catch {
      return false
    }
  }
}
